import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../api.service';
import { RequestHandlerParams } from '../types/api-service.types';
import {
  DonationCheckoutData,
  DonationCheckoutRequest,
  DonationCheckoutResult,
  DonationErrorCode
} from './donation.types';

/**
 * The donation checkout of DEV-10862. Separate from `tips-donations.service`, which drives the
 * donation that rides inside the week's order and is unchanged.
 */
@Injectable({ providedIn: 'root' })
export class DonationService {

  #apiService = inject(ApiService);

  endpoints = {
    checkout: '/donations/checkout'
  };

  /**
   * Charges a one-off donation. The session is optional: without one the donor is a guest and
   * `donor` carries who they are.
   *
   * Asks the api layer for the error rather than a toast (`returnError`, `showErrorMessage`
   * off) because this flow owns every message it shows — thirteen rejections, each with its own
   * recovery, and one of them (202) that is not an error at all. A generic toast on top of that
   * would contradict the modal.
   */
  checkout(payload: DonationCheckoutRequest): Observable<DonationCheckoutResult> {
    const params: RequestHandlerParams = {
      method: 'POST',
      endpoint: this.endpoints.checkout,
      body: payload,
      apiV3: true,
      showErrorMessage: false,
      returnError: true
    };

    return this.#apiService.handleRequest<any>(params).pipe(
      map(response => this.#toResult(response))
    );
  }

  #toResult(response: any): DonationCheckoutResult {
    if (response?.error) {
      return {
        ok: false,
        code: (response.code as DonationErrorCode) ?? null,
        message: response.message || 'Something went wrong. Please try again.',
        status: response.status ?? 0,
        retryAfter: typeof response.retryAfter === 'number' ? response.retryAfter : null
      };
    }

    const data = (response?.data ?? null) as DonationCheckoutData | null;

    return {
      ok: true,
      // 202: taken, waiting on Authorize.Net. The caller must not invite a retry — the charge
      // can still be approved, and a second attempt would be a second donation.
      held: data?.status === 'held',
      message: response?.message || 'Thank you! Your donation has been successfully processed.',
      data
    };
  }
}
